import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Flex,
  Card,
  CardHeader,
  CardBody,
  useColorModeValue,
  Icon,
  Button,
  Badge,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Collapse,
  Divider,
  Input,
  FormControl,
  FormLabel,
  SimpleGrid,
  Portal,
} from '@chakra-ui/react';
import { useCustomToast } from '../../utils/customToast';
import {
  FaCheckCircle,
  FaClock,
  FaBan,
  FaSnowflake,
  FaPlay,
  FaEllipsisV,
  FaStar,
  FaRegStar,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaComment,
  FaCalendarAlt,
  FaGlobe,
  FaSmile,
  FaExclamationTriangle,
  FaQuoteLeft,
  FaLightbulb,
  FaListAlt,
  FaSpinner,
  FaRocket,
  FaTools,
  FaUser,
} from 'react-icons/fa';
import { getActions, updateAction, generateActionsFromReviews } from '../../services/playbookService';
import { logger } from '../../utils/logger';
import { useUser } from '../../contexts/UserContext';
import { SectionLabel } from '../dashboards/DashboardWrapper';

// Goal metric configuration
const GOAL_METRICS = {
  review_frequency: {
    label: 'Review Frequency',
    icon: FaCalendarAlt,
    color: 'purple'
  },
  total_reviews: {
    label: 'Total Reviews',
    icon: FaStar,
    color: 'yellow'
  },
  traffic: {
    label: 'Traffic/SEO',
    icon: FaGlobe,
    color: 'blue'
  },
  sentiment: {
    label: 'Sentiment',
    icon: FaSmile,
    color: 'green'
  },
};

const PlaybookActionsPage = ({ sectionLabel, demoMode, demoData } = {}) => {
  const { favoriteUris } = useUser();
  const [actions, setActions] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('operational_change');
  const [goalMetricSort, setGoalMetricSort] = useState('all');
  const [expandedActions, setExpandedActions] = useState({});
  const [selectedAction, setSelectedAction] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const { isOpen: isFeedbackOpen, onOpen: onFeedbackOpen, onClose: onFeedbackClose } = useDisclosure();
  const { isOpen: isSnoozeOpen, onOpen: onSnoozeOpen, onClose: onSnoozeClose } = useDisclosure();
  const toast = useCustomToast();

  // Theme colors - all useColorModeValue calls must be at top level
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const primaryColor = useColorModeValue('blue.600', 'blue.400');
  const cardShadow = useColorModeValue('0 4px 20px rgba(0,0,0,0.08)', '0 4px 20px rgba(0,0,0,0.3)');
  const cardHoverShadow = useColorModeValue('0 8px 30px rgba(0,0,0,0.12)', '0 8px 30px rgba(0,0,0,0.4)');
  
  // Additional color mode values for components
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const filterLabelBg = useColorModeValue('gray.100', 'gray.700');
  const evidenceBg = useColorModeValue('red.50', 'red.900');
  const loadingBg = useColorModeValue('blue.50', 'blue.900');
  const emptyStateBg = useColorModeValue('blue.50', 'blue.900');
  
  // Badge backgrounds
  const blueBadgeBg = useColorModeValue('blue.50', 'blue.900');
  const greenBadgeBg = useColorModeValue('green.50', 'green.900');
  const orangeBadgeBg = useColorModeValue('orange.50', 'orange.900');
  const purpleBadgeBg = useColorModeValue('purple.50', 'purple.900');
  const yellowBadgeBg = useColorModeValue('yellow.50', 'yellow.900');
  const redBadgeBg = useColorModeValue('red.50', 'red.900');
  const tealBadgeBg = useColorModeValue('teal.50', 'teal.900');
  const grayBadgeBg = useColorModeValue('gray.50', 'gray.700');
  
  // Get badge background by color scheme
  const getBadgeBg = (colorScheme) => {
    const badgeBgMap = {
      blue: blueBadgeBg,
      green: greenBadgeBg,
      orange: orangeBadgeBg,
      purple: purpleBadgeBg,
      yellow: yellowBadgeBg,
      red: redBadgeBg,
      teal: tealBadgeBg,
      gray: grayBadgeBg,
    };
    return badgeBgMap[colorScheme] || grayBadgeBg;
  };

  // Priority colors - positive/encouraging scheme
  const getPriorityColor = (priority) => {
    if (priority <= 1) return 'blue';
    if (priority <= 2) return 'teal';
    if (priority <= 3) return 'green';
    if (priority <= 4) return 'gray';
    return 'gray';
  };

  const getPriorityLabel = (priority) => {
    if (priority <= 1) return 'Top Priority';
    if (priority <= 2) return 'Important';
    if (priority <= 3) return 'Recommended';
    if (priority <= 4) return 'When Ready';
    return 'Optional';
  };

  // Status colors and icons
  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return { color: 'green', icon: FaCheckCircle, label: 'Completed' };
      case 'in_progress':
        return { color: 'blue', icon: FaPlay, label: 'In Progress' };
      case 'dismissed':
        return { color: 'gray', icon: FaBan, label: 'Dismissed' };
      case 'snoozed':
        return { color: 'purple', icon: FaSnowflake, label: 'Snoozed' };
      default:
        return { color: 'orange', icon: FaClock, label: 'Pending' };
    }
  };

  // Effort and impact badges
  const getEffortColor = (effort) => {
    switch (effort) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'red';
      default: return 'gray';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'green';
      case 'medium': return 'yellow';
      case 'low': return 'gray';
      default: return 'gray';
    }
  };

  // Get emoji based on action type
  const getActionEmoji = (actionType) => {
    if (actionType === 'operational_change') {
      return '🔧'; // Wrench for Operations
    } else if (actionType === 'listing_improvement') {
      return '📍'; // Pin drop for Listing Management
    }
    return '🎯'; // Default fallback
  };

  // Fetch actions (only shows loading spinner on initial load)
  const fetchActions = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setIsInitialLoading(true);
    }
    try {
      const options = {};
      if (goalMetricSort !== 'all') {
        options.sortByGoal = goalMetricSort;
      }
      const actionsData = await getActions(options);
      setActions(actionsData || []);
      logger.info('PlaybookActionsPage: Loaded actions', { count: actionsData?.length });
    } catch (error) {
      logger.error('PlaybookActionsPage: Error loading actions', error);
      if (isInitial) {
        toast({
          title: 'Error loading actions',
          description: error.message || 'Please try again later',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setActions([]);
      }
    } finally {
      if (isInitial) {
        setIsInitialLoading(false);
      }
    }
  }, [toast, goalMetricSort]);

  useEffect(() => {
    if (demoMode && demoData) {
      setActions(demoData.actions || []);
      setIsInitialLoading(false);
      return;
    }
    fetchActions(true);
  }, [demoMode, demoData, fetchActions]);

  // Handle status change with optimistic update
  const handleStatusChange = async (action, newStatus) => {
    const oldStatus = action.status;

    // Optimistically update local state immediately
    setActions(prev => prev.map(a =>
      a.action_id === action.action_id ? { ...a, status: newStatus } : a
    ));

    try {
      await updateAction(action.action_id, { status: newStatus });
      toast({
        title: 'Status updated',
        description: `Action marked as ${newStatus.replace('_', ' ')}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      logger.error('PlaybookActionsPage: Error updating status', error);
      // Revert optimistic update on failure
      setActions(prev => prev.map(a =>
        a.action_id === action.action_id ? { ...a, status: oldStatus } : a
      ));
      toast({
        title: 'Error updating status',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle snooze with optimistic update
  const handleSnooze = async () => {
    if (!selectedAction || !snoozeDate) return;

    const actionToSnooze = selectedAction;
    const oldStatus = actionToSnooze.status;
    const oldSnoozedUntil = actionToSnooze.snoozed_until;
    const newSnoozedUntil = new Date(snoozeDate).toISOString();

    // Close modal immediately for snappy feel
    onSnoozeClose();
    setSnoozeDate('');
    setSelectedAction(null);

    // Optimistically update local state
    setActions(prev => prev.map(a =>
      a.action_id === actionToSnooze.action_id
        ? { ...a, status: 'snoozed', snoozed_until: newSnoozedUntil }
        : a
    ));

    try {
      await updateAction(actionToSnooze.action_id, {
        snoozed_until: newSnoozedUntil
      });
      toast({
        title: 'Action snoozed',
        description: `Will resurface on ${new Date(snoozeDate).toLocaleDateString()}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      logger.error('PlaybookActionsPage: Error snoozing action', error);
      // Revert optimistic update on failure
      setActions(prev => prev.map(a =>
        a.action_id === actionToSnooze.action_id
          ? { ...a, status: oldStatus, snoozed_until: oldSnoozedUntil }
          : a
      ));
      toast({
        title: 'Error snoozing action',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle feedback with optimistic update
  const handleFeedback = async () => {
    if (!selectedAction) return;

    const actionToUpdate = selectedAction;
    const oldRating = actionToUpdate.user_feedback_rating;
    const oldText = actionToUpdate.user_feedback_text;
    const newRating = feedbackRating > 0 ? feedbackRating : undefined;
    const newText = feedbackText || undefined;

    // Close modal immediately for snappy feel
    onFeedbackClose();
    setFeedbackText('');
    setFeedbackRating(0);
    setSelectedAction(null);

    // Optimistically update local state
    setActions(prev => prev.map(a =>
      a.action_id === actionToUpdate.action_id
        ? { ...a, user_feedback_rating: newRating, user_feedback_text: newText }
        : a
    ));

    try {
      await updateAction(actionToUpdate.action_id, {
        user_feedback_rating: newRating,
        user_feedback_text: newText,
      });
      toast({
        title: 'Feedback submitted',
        description: 'Thank you for your feedback!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      logger.error('PlaybookActionsPage: Error submitting feedback', error);
      // Revert optimistic update on failure
      setActions(prev => prev.map(a =>
        a.action_id === actionToUpdate.action_id
          ? { ...a, user_feedback_rating: oldRating, user_feedback_text: oldText }
          : a
      ));
      toast({
        title: 'Error submitting feedback',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Generate AI action recommendations from review analysis.
  // Used when the actions collection is empty for the user's vertical
  // (no admin-generated batch yet) so the page isn't a dead end.
  const handleGenerateActions = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const result = await generateActionsFromReviews({ maxActions: 10 });
      logger.info('PlaybookActionsPage: Generated actions', result);
      toast({
        title: 'Recommendations generated',
        description: `Created ${result?.actions_generated ?? ''} action items based on review analysis`.trim(),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchActions(true);
    } catch (error) {
      logger.error('PlaybookActionsPage: Error generating actions', error);
      toast({
        title: 'Could not generate recommendations',
        description: error.message || 'Please try again in a moment.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Filter actions - show all market actions
  const filteredActions = actions.filter(action => {
    if (statusFilter !== 'all' && action.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && action.priority > parseInt(priorityFilter)) return false;
    if (typeFilter !== 'all' && action.type !== typeFilter) return false;
    return true;
  });

  const toggleExpanded = (actionId) => {
    setExpandedActions(prev => ({
      ...prev,
      [actionId]: !prev[actionId]
    }));
  };

  // Loading state (only shows on initial load)
  if (isInitialLoading) {
    return (
      <Box w="100%" py={6} px={{ base: 3, md: 6 }}>
        {/* Section Label */}
        {!demoMode && <SectionLabel description="Actionable recommendations based on your competitive data. Get specific next steps to improve rankings, sentiment, and market share.">
          Playbook: Actions
        </SectionLabel>}

        <Flex 
          justify="center" 
          align="center" 
          minH="500px" 
          w="100%"
          direction="column"
          gap={4}
        >
          <Box
            p={6}
            borderRadius="full"
            bg={loadingBg}
          >
            <Spinner 
              size="xl" 
              thickness="4px" 
              speed="0.65s" 
              emptyColor="gray.200" 
              color={primaryColor} 
            />
          </Box>
          <Text fontSize="lg" color={subtleTextColor} fontWeight="medium">
            Loading Actions...
          </Text>
        </Flex>
      </Box>
    );
  }

  // Empty state
  if (!actions.length) {
    return (
      <Box w="100%" py={6} px={{ base: 3, md: 6 }}>
        {/* Section Label */}
        {!demoMode && <SectionLabel description="Actionable recommendations based on your competitive data. Get specific next steps to improve rankings, sentiment, and market share.">
          Playbook: Actions
        </SectionLabel>}

        <Flex
          direction="column"
          align="center"
          justify="center"
          p={12}
          borderRadius="2xl"
          bg={cardBg}
          minH="400px"
          boxShadow={cardShadow}
          position="relative"
          overflow="hidden"
        >
          {/* Decorative background gradient */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="4px"
            bgGradient="linear(to-r, blue.400, purple.400, pink.400)"
          />
          <Flex
            w="80px"
            h="80px"
            borderRadius="2xl"
            bg={emptyStateBg}
            justify="center"
            align="center"
            mb={6}
          >
            <Icon as={FaRocket} boxSize={8} color="blue.400" />
          </Flex>
          <Heading size="lg" color={textColor} mb={3} textAlign="center">
            No Actions Available
          </Heading>
          <Text color={subtleTextColor} textAlign="center" maxW="440px" lineHeight="tall" mb={6}>
            We haven't analyzed your market's reviews into action recommendations yet.
            Click below to generate a fresh playbook based on your competitive review data.
          </Text>
          <Button
            size="lg"
            bgGradient="linear(to-r, blue.500, purple.500)"
            color="white"
            leftIcon={isGenerating ? <Spinner size="sm" /> : <Icon as={FaRocket} boxSize={3} />}
            onClick={handleGenerateActions}
            isLoading={isGenerating}
            loadingText="Analyzing reviews..."
            borderRadius="xl"
            px={8}
            py={6}
            _hover={{
              bgGradient: 'linear(to-r, blue.600, purple.600)',
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
            }}
            transition="all 0.2s"
          >
            Generate Recommendations
          </Button>
          {isGenerating && (
            <Text color={subtleTextColor} textAlign="center" maxW="380px" mt={4} fontSize="sm">
              This usually takes 20-40 seconds while the AI reads through reviews.
            </Text>
          )}
        </Flex>
      </Box>
    );
  }

  // Action Card Component - designed for grid layout with enhanced aesthetics
  const ActionCard = ({ action }) => {
    const statusInfo = getStatusInfo(action.status);
    const isExpanded = expandedActions[action.action_id];
    const cardAccentColor = 'blue'; // Used for card accent styling

    return (
      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        boxShadow={cardShadow}
        _hover={{
          boxShadow: cardHoverShadow,
          borderColor: `${cardAccentColor}.300`,
          transform: 'translateY(-4px)',
        }}
        opacity={action.status === 'dismissed' ? 0.6 : 1}
        overflow="hidden"
        h="100%"
        display="flex"
        flexDirection="column"
        position="relative"
      >
        {/* Accent indicator bar with gradient */}
        <Box
          h="4px"
          bgGradient={`linear(to-r, ${cardAccentColor}.400, ${cardAccentColor}.500)`}
          w="100%"
        />

        <CardHeader pb={2} pt={5} px={5}>
          <Flex justify="space-between" align="flex-start" gap={2}>
            <Box flex={1} minW={0}>
              <HStack spacing={2} mb={3} flexWrap="wrap">
                <Badge 
                  bg={getBadgeBg(statusInfo.color)}
                  color={`${statusInfo.color}.600`}
                  fontSize="xs" 
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  <Flex align="center" gap={1.5}>
                    <Icon as={statusInfo.icon} boxSize={2.5} />
                    <Text as="span" fontWeight="medium">{statusInfo.label}</Text>
                  </Flex>
                </Badge>
              </HStack>
              {/* Goal Metric Badges */}
              {action.goal_metrics && action.goal_metrics.length > 0 && (
                <HStack spacing={2} mb={3} flexWrap="wrap">
                  {action.goal_metrics.map(metric => {
                    const config = GOAL_METRICS[metric];
                    if (!config) return null;
                    return (
                      <Tooltip key={metric} label={`Helps improve: ${config.label}`} hasArrow>
                        <Badge
                          bg={getBadgeBg(config.color)}
                          color={`${config.color}.600`}
                          fontSize="xs"
                          borderRadius="full"
                          px={2}
                          py={0.5}
                        >
                          <Flex align="center" gap={1}>
                            <Icon as={config.icon} boxSize={2.5} />
                            <Text as="span">{config.label}</Text>
                          </Flex>
                        </Badge>
                      </Tooltip>
                    );
                  })}
                </HStack>
              )}
              <Text 
                fontWeight="bold" 
                color={textColor} 
                fontSize="2xl" 
                noOfLines={2} 
                lineHeight="tall"
                mb={1}
              >
                {getActionEmoji(action.type)} {action.title}
              </Text>
              <Text 
                fontSize="xs" 
                color={subtleTextColor} 
                textTransform="capitalize"
                fontWeight="medium"
                bg={filterLabelBg}
                px={2}
                py={0.5}
                borderRadius="md"
                display="inline-block"
              >
                {action.type?.replace(/_/g, ' ')}
              </Text>
            </Box>

            <Menu isLazy>
              <MenuButton
                as={IconButton}
                icon={<Icon as={FaEllipsisV} />}
                variant="ghost"
                size="sm"
                color="gray.400"
                aria-label="More options"
                borderRadius="full"
                _hover={{ bg: filterLabelBg, color: textColor }}
              />
              <Portal>
                <MenuList zIndex={1500} shadow="xl" borderRadius="xl" py={2}>
                  {action.status !== 'in_progress' && (
                    <MenuItem
                      icon={<Icon as={FaPlay} color="blue.500" />}
                      onClick={() => handleStatusChange(action, 'in_progress')}
                      borderRadius="md"
                      mx={2}
                      _hover={{ bg: 'blue.50' }}
                    >
                      Start Working
                    </MenuItem>
                  )}
                  {action.status !== 'completed' && (
                    <MenuItem
                      icon={<Icon as={FaCheckCircle} color="green.500" />}
                      onClick={() => handleStatusChange(action, 'completed')}
                      borderRadius="md"
                      mx={2}
                      _hover={{ bg: 'green.50' }}
                    >
                      Mark Complete
                    </MenuItem>
                  )}
                  <MenuItem
                    icon={<Icon as={FaSnowflake} color="purple.500" />}
                    onClick={() => {
                      setSelectedAction(action);
                      setSnoozeDate('');
                      onSnoozeOpen();
                    }}
                    borderRadius="md"
                    mx={2}
                    _hover={{ bg: 'purple.50' }}
                  >
                    Snooze
                  </MenuItem>
                  <MenuItem
                    icon={<Icon as={FaComment} color="blue.500" />}
                    onClick={() => {
                      setSelectedAction(action);
                      setFeedbackRating(action.user_feedback_rating || 0);
                      setFeedbackText(action.user_feedback_text || '');
                      onFeedbackOpen();
                    }}
                    borderRadius="md"
                    mx={2}
                    _hover={{ bg: 'blue.50' }}
                  >
                    Give Feedback
                  </MenuItem>
                  <Divider my={2} />
                  {action.status !== 'dismissed' ? (
                    <MenuItem
                      icon={<Icon as={FaBan} color="red.500" />}
                      onClick={() => handleStatusChange(action, 'dismissed')}
                      borderRadius="md"
                      mx={2}
                      _hover={{ bg: 'red.50' }}
                    >
                      Dismiss
                    </MenuItem>
                  ) : (
                    <MenuItem
                      icon={<Icon as={FaClock} color="orange.500" />}
                      onClick={() => handleStatusChange(action, 'pending')}
                      borderRadius="md"
                      mx={2}
                      _hover={{ bg: 'orange.50' }}
                    >
                      Restore
                    </MenuItem>
                  )}
                </MenuList>
              </Portal>
            </Menu>
          </Flex>
        </CardHeader>

        <CardBody pt={0} px={5} pb={5} flex={1} display="flex" flexDirection="column">
          <Text 
            color={textColor} 
            fontSize="md" 
            noOfLines={isExpanded ? undefined : 3} 
            mb={4}
            lineHeight="tall"
          >
            {action.description}
          </Text>

          {/* Effort/Impact badges - enhanced design */}
          <HStack spacing={3} mb={4} flexWrap="wrap">
            <Flex
              align="center"
              gap={2}
              bg={getBadgeBg(getEffortColor(action.estimated_effort))}
              px={3}
              py={1.5}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={`${getEffortColor(action.estimated_effort)}.200`}
            >
              <Text fontSize="xs" color={subtleTextColor} fontWeight="medium">Effort:</Text>
              <Text 
                fontSize="xs" 
                color={`${getEffortColor(action.estimated_effort)}.600`} 
                fontWeight="bold"
                textTransform="uppercase"
              >
                {action.estimated_effort}
              </Text>
            </Flex>
            <Flex
              align="center"
              gap={2}
              bg={getBadgeBg(getImpactColor(action.expected_impact))}
              px={3}
              py={1.5}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={`${getImpactColor(action.expected_impact)}.200`}
            >
              <Text fontSize="xs" color={subtleTextColor} fontWeight="medium">Impact:</Text>
              <Text 
                fontSize="xs" 
                color={`${getImpactColor(action.expected_impact)}.600`} 
                fontWeight="bold"
                textTransform="uppercase"
              >
                {action.expected_impact}
              </Text>
            </Flex>
          </HStack>

          <Collapse in={isExpanded} animateOpacity>
            <VStack align="stretch" spacing={3} pb={2}>
              {action.success_metric && (
                <Box 
                  bg={blueBadgeBg} 
                  p={3} 
                  borderRadius="lg"
                  borderLeftWidth="3px"
                  borderLeftColor="blue.400"
                >
                  <Text fontSize="xs" color={subtleTextColor} fontWeight="medium" mb={1}>Success Metric</Text>
                  <Text fontSize="sm" color={textColor} fontWeight="semibold">
                    {action.success_metric.replace(/_/g, ' ')}
                  </Text>
                </Box>
              )}

              {/* Review-Based Evidence Section */}
              {action.evidence && typeof action.evidence === 'object' && !Array.isArray(action.evidence) && Object.keys(action.evidence).length > 0 && (
                <Box
                  bg={evidenceBg}
                  p={4}
                  borderRadius="lg"
                  borderLeftWidth="3px"
                  borderLeftColor="red.400"
                >
                  <HStack mb={3} spacing={2}>
                    <Flex
                      w="24px"
                      h="24px"
                      borderRadius="full"
                      bg="red.100"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaExclamationTriangle} color="red.500" boxSize={3} />
                    </Flex>
                    <Text fontSize="xs" fontWeight="bold" color="red.600" textTransform="uppercase" letterSpacing="0.5px">
                      Based on Customer Reviews
                    </Text>
                  </HStack>

                  {/* Negative review count and theme */}
                  {action.evidence.negative_review_count && (
                    <Text fontSize="sm" color={textColor} mb={2}>
                      <Text as="span" fontWeight="bold" color="red.600">
                        {action.evidence.negative_review_count}
                      </Text>
                      {' negative reviews about '}
                      <Text as="span" fontWeight="semibold">
                        {action.evidence.theme || 'this issue'}
                      </Text>
                    </Text>
                  )}

                  {/* Affected businesses */}
                  {action.evidence.businesses_affected && action.evidence.businesses_affected.length > 0 && (
                    <Text fontSize="xs" color={subtleTextColor} mb={2}>
                      Seen at: {action.evidence.businesses_affected.slice(0, 3).join(', ')}
                      {action.evidence.businesses_affected.length > 3 && ` +${action.evidence.businesses_affected.length - 3} more`}
                    </Text>
                  )}

                  {/* Sample quotes */}
                  {action.evidence.sample_quotes && action.evidence.sample_quotes.length > 0 && (
                    <VStack align="stretch" spacing={2} mt={3}>
                      {action.evidence.sample_quotes.slice(0, 2).map((quote, idx) => (
                        <HStack key={idx} align="flex-start" spacing={2} bg="white" p={2} borderRadius="md">
                          <Icon as={FaQuoteLeft} color="red.300" boxSize={3} mt={0.5} flexShrink={0} />
                          <Text fontSize="xs" color={subtleTextColor} fontStyle="italic">
                            "{quote}"
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </Box>
              )}

              {/* Cited Reviews - structured review citations */}
              {action.cited_reviews && action.cited_reviews.length > 0 && (
                <Box
                  bg={blueBadgeBg}
                  p={4}
                  borderRadius="lg"
                  borderLeftWidth="3px"
                  borderLeftColor="blue.400"
                >
                  <HStack mb={3} spacing={2}>
                    <Flex
                      w="24px"
                      h="24px"
                      borderRadius="full"
                      bg="blue.100"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaUser} color="blue.500" boxSize={3} />
                    </Flex>
                    <Text fontSize="xs" fontWeight="bold" color="blue.600" textTransform="uppercase" letterSpacing="0.5px">
                      Review Citations
                    </Text>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {action.cited_reviews.slice(0, 3).map((cite, idx) => (
                      <Box key={idx} bg="white" p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                        <HStack justify="space-between" mb={1.5}>
                          <HStack spacing={2}>
                            <Icon as={FaQuoteLeft} boxSize={3} color="gray.300" />
                            <Text fontSize="xs" fontWeight="600" color={textColor}>
                              {cite.reviewer_name}
                            </Text>
                          </HStack>
                          <HStack spacing={0.5}>
                            {[...Array(5)].map((_, i) => (
                              <Icon
                                key={i}
                                as={i < cite.stars ? FaStar : FaRegStar}
                                boxSize={3}
                                color={i < cite.stars ? 'yellow.400' : 'gray.200'}
                              />
                            ))}
                          </HStack>
                        </HStack>
                        <Text fontSize="xs" color={subtleTextColor} fontStyle="italic" mb={1}>
                          "{cite.text_snippet}"
                        </Text>
                        {cite.relevance && (
                          <Text fontSize="2xs" color="blue.500" fontWeight="medium">
                            {cite.relevance}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* AI-generated insight indicator */}
              {action.source === 'ai_review_analysis' && (
                <HStack 
                  spacing={2} 
                  pt={1}
                  bg={yellowBadgeBg}
                  p={2}
                  borderRadius="lg"
                >
                  <Icon as={FaLightbulb} color="yellow.500" boxSize={3} />
                  <Text fontSize="xs" color={subtleTextColor} fontWeight="medium">
                    AI-generated from review analysis
                  </Text>
                </HStack>
              )}

              {action.user_feedback_rating && (
                <HStack bg={grayBadgeBg} p={2} borderRadius="lg">
                  <Text fontSize="xs" color={subtleTextColor} fontWeight="medium">Your rating:</Text>
                  <HStack spacing={0.5}>
                    {[1, 2, 3, 4, 5].map(star => (
                      <Icon
                        key={star}
                        as={star <= action.user_feedback_rating ? FaStar : FaRegStar}
                        color={star <= action.user_feedback_rating ? 'yellow.400' : 'gray.300'}
                        boxSize={3}
                      />
                    ))}
                  </HStack>
                </HStack>
              )}

              {action.snoozed_until && action.status === 'snoozed' && (
                <Flex 
                  align="center" 
                  gap={2}
                  bg={purpleBadgeBg}
                  p={2}
                  borderRadius="lg"
                >
                  <Icon as={FaCalendarAlt} color="purple.500" boxSize={3} flexShrink={0} />
                  <Text as="span" fontSize="xs" color="purple.600" fontWeight="medium">
                    Snoozed until {new Date(action.snoozed_until).toLocaleDateString()}
                  </Text>
                </Flex>
              )}

              <Text fontSize="xs" color={subtleTextColor}>
                Created {new Date(action.created_at).toLocaleDateString()}
              </Text>
            </VStack>
          </Collapse>

          {/* Action buttons - pushed to bottom with enhanced styling */}
          <Box 
            mt="auto" 
            pt={4} 
            borderTopWidth="1px" 
            borderColor={borderColor}
          >
            <HStack spacing={2} justify="space-between">
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Icon as={isExpanded ? FaChevronUp : FaChevronDown} boxSize={2.5} />}
                onClick={() => toggleExpanded(action.action_id)}
                color={subtleTextColor}
                fontWeight="medium"
                borderRadius="lg"
                _hover={{ bg: hoverBg }}
              >
                {isExpanded ? 'Less' : 'More'}
              </Button>

              <HStack spacing={2}>
                {action.status === 'pending' && (
                  <Button
                    size="sm"
                    bgGradient="linear(to-r, blue.500, blue.600)"
                    color="white"
                    leftIcon={<Icon as={FaPlay} boxSize={2.5} />}
                    onClick={() => handleStatusChange(action, 'in_progress')}
                    borderRadius="lg"
                    _hover={{
                      bgGradient: 'linear(to-r, blue.600, blue.700)',
                      transform: 'scale(1.02)',
                    }}
                    transition="all 0.2s"
                  >
                    Start
                  </Button>
                )}
                {action.status === 'in_progress' && (
                  <Button
                    size="sm"
                    bgGradient="linear(to-r, green.500, green.600)"
                    color="white"
                    leftIcon={<Icon as={FaCheckCircle} boxSize={2.5} />}
                    onClick={() => handleStatusChange(action, 'completed')}
                    borderRadius="lg"
                    _hover={{
                      bgGradient: 'linear(to-r, green.600, green.700)',
                      transform: 'scale(1.02)',
                    }}
                    transition="all 0.2s"
                  >
                    Complete
                  </Button>
                )}
              </HStack>
            </HStack>
          </Box>
        </CardBody>
      </Card>
    );
  };

  // Summary stats for header - all market actions
  const stats = {
    total: actions.length,
    pending: actions.filter(a => a.status === 'pending').length,
    inProgress: actions.filter(a => a.status === 'in_progress').length,
    completed: actions.filter(a => a.status === 'completed').length,
  };

  // Stats card configuration with colors and icons
  const statsConfig = [
    {
      key: 'total',
      label: 'Total Actions',
      value: stats.total,
      icon: FaListAlt,
      gradient: 'linear(to-br, blue.400, blue.600)',
      lightBg: 'linear(to-br, blue.50, white)',
      borderColor: 'blue.200',
      iconBg: 'blue.100',
      textColor: 'blue.600',
    },
    {
      key: 'pending',
      label: 'Pending',
      value: stats.pending,
      icon: FaClock,
      gradient: 'linear(to-br, orange.400, orange.600)',
      lightBg: 'linear(to-br, orange.50, white)',
      borderColor: 'orange.200',
      iconBg: 'orange.100',
      textColor: 'orange.500',
    },
    {
      key: 'inProgress',
      label: 'In Progress',
      value: stats.inProgress,
      icon: FaSpinner,
      gradient: 'linear(to-br, purple.400, purple.600)',
      lightBg: 'linear(to-br, purple.50, white)',
      borderColor: 'purple.200',
      iconBg: 'purple.100',
      textColor: 'purple.500',
    },
    {
      key: 'completed',
      label: 'Completed',
      value: stats.completed,
      icon: FaCheckCircle,
      gradient: 'linear(to-br, green.400, green.600)',
      lightBg: 'linear(to-br, green.50, white)',
      borderColor: 'green.200',
      iconBg: 'green.100',
      textColor: 'green.500',
    },
  ];

  // Action type card configuration
  const actionTypeConfig = {
    'operational_change': {
      label: 'Operational Improvements',
      icon: FaTools,
      color: 'blue',
      gradient: 'linear(to-br, blue.400, blue.600)',
      lightGradient: 'linear(to-br, blue.50, blue.100)',
      description: 'Optimize your business operations',
    },
    'competitive_move': {
      label: 'Compete Smarter',
      icon: FaRocket,
      color: 'purple',
      gradient: 'linear(to-br, purple.400, purple.600)',
      lightGradient: 'linear(to-br, purple.50, purple.100)',
      description: 'Stay ahead of your competition',
    },
    'listing_improvement': {
      label: 'Improve Visibility',
      icon: FaGlobe,
      color: 'green',
      gradient: 'linear(to-br, green.400, green.600)',
      lightGradient: 'linear(to-br, green.50, green.100)',
      description: 'Get found by more customers',
    },
  };

  return (
    <Box w="100%" py={6} px={{ base: 3, md: 6 }}>
      {/* Page Header */}
      <Box mb={10} textAlign="center">
        <Heading
          fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
          fontWeight="extrabold"
          bgGradient="linear(to-r, blue.500, purple.500, pink.500)"
          bgClip="text"
          mb={2}
        >
          How to Win
        </Heading>
        <Text
          fontSize={{ base: 'md', md: 'lg' }}
          color={subtleTextColor}
          fontWeight="medium"
        >
          Playbook Actions
        </Text>
      </Box>

      {/* Action Type Selection - Large Animated Cards */}
      <Box mb={10}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 6 }} maxW="1200px" mx="auto">
          {['operational_change', 'competitive_move', 'listing_improvement'].map((type) => {
            const config = actionTypeConfig[type];
            const isSelected = typeFilter === type;
            const actionCount = actions.filter(a => a.type === type).length;
            
            return (
              <Box
                key={type}
                onClick={() => setTypeFilter(isSelected ? 'all' : type)}
                cursor="pointer"
                position="relative"
                transform={isSelected ? 'scale(1.08)' : 'scale(1)'}
                transition="all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
                zIndex={isSelected ? 10 : 1}
                _hover={{
                  transform: isSelected ? 'scale(1.1)' : 'scale(1.03)',
                }}
              >
                <Box
                  bg={isSelected ? 'white' : cardBg}
                  borderRadius="2xl"
                  p={{ base: 6, md: 8 }}
                  borderWidth={isSelected ? '3px' : '2px'}
                  borderColor={isSelected ? `${config.color}.500` : borderColor}
                  boxShadow={isSelected ? `0 20px 40px -10px var(--chakra-colors-${config.color}-300)` : cardShadow}
                  transition="all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
                  overflow="hidden"
                  position="relative"
                  _hover={{
                    borderColor: `${config.color}.400`,
                    boxShadow: isSelected 
                      ? `0 25px 50px -10px var(--chakra-colors-${config.color}-400)` 
                      : `0 10px 30px -5px var(--chakra-colors-${config.color}-200)`,
                  }}
                >
                  {/* Gradient overlay when selected */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    h={isSelected ? '6px' : '4px'}
                    bgGradient={config.gradient}
                    transition="all 0.3s ease"
                  />
                  
                  {/* Icon */}
                  <Flex
                    w={isSelected ? '80px' : '60px'}
                    h={isSelected ? '80px' : '60px'}
                    borderRadius="2xl"
                    bgGradient={isSelected ? config.gradient : config.lightGradient}
                    justify="center"
                    align="center"
                    mb={4}
                    mx="auto"
                    transition="all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
                    boxShadow={isSelected ? `0 10px 20px -5px var(--chakra-colors-${config.color}-300)` : 'none'}
                  >
                    <Icon 
                      as={config.icon} 
                      boxSize={isSelected ? 8 : 6} 
                      color={isSelected ? 'white' : `${config.color}.500`}
                      transition="all 0.3s ease"
                    />
                  </Flex>
                  
                  {/* Label */}
                  <Text
                    fontSize={isSelected ? '2xl' : 'xl'}
                    fontWeight="bold"
                    color={isSelected ? `${config.color}.600` : textColor}
                    textAlign="center"
                    mb={2}
                    transition="all 0.3s ease"
                  >
                    {config.label}
                  </Text>
                  
                  {/* Description */}
                  <Text
                    fontSize={isSelected ? 'md' : 'sm'}
                    color={subtleTextColor}
                    textAlign="center"
                    mb={4}
                    transition="all 0.3s ease"
                  >
                    {config.description}
                  </Text>
                  
                  {/* Action count badge */}
                  <Flex justify="center">
                    <Badge
                      colorScheme={config.color}
                      fontSize={isSelected ? 'md' : 'sm'}
                      px={4}
                      py={2}
                      borderRadius="full"
                      variant={isSelected ? 'solid' : 'subtle'}
                      transition="all 0.3s ease"
                    >
                      {actionCount} {actionCount === 1 ? 'action' : 'actions'}
                    </Badge>
                  </Flex>
                  
                  {/* Selected indicator */}
                  {isSelected && (
                    <Flex
                      position="absolute"
                      top={3}
                      right={3}
                      w="28px"
                      h="28px"
                      borderRadius="full"
                      bgGradient={config.gradient}
                      justify="center"
                      align="center"
                      boxShadow="md"
                    >
                      <Icon as={FaCheckCircle} color="white" boxSize={4} />
                    </Flex>
                  )}
                </Box>
              </Box>
            );
          })}
        </SimpleGrid>
      </Box>

      {/* Section Label */}
      {!demoMode && <SectionLabel description="Actionable recommendations based on your competitive data. Get specific next steps to improve rankings, sentiment, and market share.">
        Playbook: Actions
      </SectionLabel>}

      {/* Stats Cards */}
      <Box mb={8}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {statsConfig.map((stat) => (
            <Box
              key={stat.key}
              bg={cardBg}
              p={5}
              borderRadius="xl"
              borderWidth="1px"
              borderColor={stat.borderColor}
              boxShadow={cardShadow}
              transition="all 0.3s ease"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: cardHoverShadow,
              }}
              position="relative"
              overflow="hidden"
            >
              <Flex justify="space-between" align="flex-start">
                <Box>
                  <Text
                    fontSize={{ base: '2xl', md: '3xl' }}
                    fontWeight="bold"
                    color={stat.textColor}
                    lineHeight="1"
                    mb={1}
                  >
                    {stat.value}
                  </Text>
                  <Text fontSize="sm" color={subtleTextColor} fontWeight="medium">
                    {stat.label}
                  </Text>
                </Box>
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="lg"
                  bg={stat.iconBg}
                  justify="center"
                  align="center"
                >
                  <Icon as={stat.icon} color={stat.textColor} boxSize={4} />
                </Flex>
              </Flex>
              {/* Decorative gradient bar */}
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h="3px"
                bgGradient={stat.gradient}
              />
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Filters Section */}
      <Box
        bg={cardBg}
        borderRadius="xl"
        p={4}
        mb={6}
        boxShadow={cardShadow}
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', lg: 'center' }}
          gap={4}
        >
          {/* Filter Label */}
          <HStack spacing={3} flexWrap="wrap">
            <Flex 
              align="center" 
              gap={2}
              bg={filterLabelBg}
              px={3}
              py={1.5}
              borderRadius="lg"
            >
              <Icon as={FaFilter} color={subtleTextColor} boxSize={3} />
              <Text as="span" fontSize="sm" color={subtleTextColor} fontWeight="medium">Filter:</Text>
            </Flex>
            <Select
              size="sm"
              w="150px"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              bg={inputBg}
              borderRadius="lg"
              borderColor={borderColor}
              fontWeight="medium"
              _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="snoozed">Snoozed</option>
              <option value="dismissed">Dismissed</option>
            </Select>
            <Select
              size="sm"
              w="150px"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              bg={inputBg}
              borderRadius="lg"
              borderColor={borderColor}
              fontWeight="medium"
              _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
            >
              <option value="all">All Priority</option>
              <option value="2">High+</option>
              <option value="3">Medium+</option>
              <option value="5">All</option>
            </Select>
            <Select
              size="sm"
              w="180px"
              value={goalMetricSort}
              onChange={(e) => setGoalMetricSort(e.target.value)}
              bg={inputBg}
              borderRadius="lg"
              borderColor={borderColor}
              fontWeight="medium"
              _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
            >
              <option value="all">All Goals</option>
              <option value="review_frequency">Review Frequency</option>
              <option value="total_reviews">Total Reviews</option>
              <option value="traffic">Traffic/SEO</option>
              <option value="sentiment">Sentiment</option>
            </Select>
          </HStack>
        </Flex>
      </Box>

      {/* Action Cards Grid */}
      <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={5}>
        {filteredActions.map(action => (
          <ActionCard key={action.action_id} action={action} />
        ))}
      </SimpleGrid>

      {filteredActions.length === 0 && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          p={12}
          bg={cardBg}
          borderRadius="2xl"
          boxShadow={cardShadow}
          minH="350px"
          position="relative"
          overflow="hidden"
        >
          {/* Decorative background */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="4px"
            bgGradient="linear(to-r, gray.300, gray.400, gray.300)"
          />
          <Flex
            w="70px"
            h="70px"
            borderRadius="2xl"
            bg={filterLabelBg}
            justify="center"
            align="center"
            mb={5}
          >
            <Icon as={FaFilter} boxSize={7} color="gray.400" />
          </Flex>
          <Text color={textColor} fontSize="xl" fontWeight="semibold" mb={2}>
            No actions match your filters
          </Text>
          <Text color={subtleTextColor} fontSize="sm" mb={6} textAlign="center" maxW="300px">
            Try adjusting your filter criteria to see more actions
          </Text>
          <Button
            variant="outline"
            colorScheme="blue"
            size="md"
            borderRadius="xl"
            px={6}
            onClick={() => {
              setStatusFilter('all');
              setPriorityFilter('all');
              setTypeFilter('all');
              setGoalMetricSort('all');
            }}
            _hover={{
              bg: 'blue.50',
              transform: 'scale(1.02)',
            }}
            transition="all 0.2s"
          >
            Clear all filters
          </Button>
        </Flex>
      )}

      {/* Snooze Modal */}
      <Modal 
        isOpen={isSnoozeOpen} 
        onClose={() => {
          setSnoozeDate('');
          setSelectedAction(null);
          onSnoozeClose();
        }}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <Box
            h="4px"
            bgGradient="linear(to-r, purple.400, purple.600)"
            borderTopRadius="2xl"
          />
          <ModalHeader pt={6} pb={2}>
            <HStack spacing={3}>
              <Flex
                w="40px"
                h="40px"
                borderRadius="xl"
                bg="purple.100"
                justify="center"
                align="center"
              >
                <Icon as={FaSnowflake} color="purple.500" boxSize={4} />
              </Flex>
              <Text>Snooze Action</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={4} right={4} />
          <ModalBody py={4}>
            <Text mb={5} color={subtleTextColor}>
              Choose when you want this action to resurface:
            </Text>
            <FormControl>
              <FormLabel fontWeight="medium">Snooze until</FormLabel>
              <Input
                type="date"
                value={snoozeDate}
                onChange={(e) => setSnoozeDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                borderRadius="lg"
                size="lg"
              />
            </FormControl>
            <HStack mt={4} spacing={2} flexWrap="wrap">
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                onClick={() => {
                  const date = new Date();
                  date.setDate(date.getDate() + 7);
                  setSnoozeDate(date.toISOString().split('T')[0]);
                }}
                _hover={{ bg: 'purple.50', borderColor: 'purple.300' }}
              >
                1 Week
              </Button>
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                onClick={() => {
                  const date = new Date();
                  date.setDate(date.getDate() + 14);
                  setSnoozeDate(date.toISOString().split('T')[0]);
                }}
                _hover={{ bg: 'purple.50', borderColor: 'purple.300' }}
              >
                2 Weeks
              </Button>
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                onClick={() => {
                  const date = new Date();
                  date.setMonth(date.getMonth() + 1);
                  setSnoozeDate(date.toISOString().split('T')[0]);
                }}
                _hover={{ bg: 'purple.50', borderColor: 'purple.300' }}
              >
                1 Month
              </Button>
            </HStack>
          </ModalBody>
          <ModalFooter pt={2} pb={6}>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={onSnoozeClose}
              borderRadius="xl"
            >
              Cancel
            </Button>
            <Button
              bgGradient="linear(to-r, purple.500, purple.600)"
              color="white"
              onClick={handleSnooze}
              isDisabled={!snoozeDate}
              leftIcon={<Icon as={FaSnowflake} />}
              borderRadius="xl"
              px={6}
              _hover={{
                bgGradient: 'linear(to-r, purple.600, purple.700)',
              }}
            >
              Snooze
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Feedback Modal */}
      <Modal isOpen={isFeedbackOpen} onClose={onFeedbackClose} isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <Box
            h="4px"
            bgGradient="linear(to-r, blue.400, blue.600)"
            borderTopRadius="2xl"
          />
          <ModalHeader pt={6} pb={2}>
            <HStack spacing={3}>
              <Flex
                w="40px"
                h="40px"
                borderRadius="xl"
                bg="blue.100"
                justify="center"
                align="center"
              >
                <Icon as={FaComment} color="blue.500" boxSize={4} />
              </Flex>
              <Text>Action Feedback</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={4} right={4} />
          <ModalBody py={4}>
            <VStack spacing={5} align="stretch">
              <Box 
                bg={grayBadgeBg} 
                p={5} 
                borderRadius="xl"
                textAlign="center"
              >
                <Text mb={3} fontWeight="medium" color={textColor}>
                  How useful was this action recommendation?
                </Text>
                <HStack spacing={1} justify="center">
                  {[1, 2, 3, 4, 5].map(star => (
                    <IconButton
                      key={star}
                      icon={<Icon as={star <= feedbackRating ? FaStar : FaRegStar} boxSize={6} />}
                      color={star <= feedbackRating ? 'yellow.400' : 'gray.300'}
                      variant="ghost"
                      size="lg"
                      onClick={() => setFeedbackRating(star)}
                      aria-label={`Rate ${star} stars`}
                      _hover={{
                        transform: 'scale(1.2)',
                        color: 'yellow.400',
                      }}
                      transition="all 0.2s"
                    />
                  ))}
                </HStack>
              </Box>
              <FormControl>
                <FormLabel fontWeight="medium">Additional feedback (optional)</FormLabel>
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Tell us what you think about this action..."
                  rows={4}
                  borderRadius="lg"
                  _focus={{
                    borderColor: 'blue.400',
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter pt={2} pb={6}>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={onFeedbackClose}
              borderRadius="xl"
            >
              Cancel
            </Button>
            <Button
              bgGradient="linear(to-r, blue.500, blue.600)"
              color="white"
              onClick={handleFeedback}
              leftIcon={<Icon as={FaComment} />}
              borderRadius="xl"
              px={6}
              _hover={{
                bgGradient: 'linear(to-r, blue.600, blue.700)',
              }}
            >
              Submit Feedback
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PlaybookActionsPage;
